import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createAdminUsersClient } from './api';
import { AdminUsersContext } from './model';
import type { AdminUser, AdminUserMutationPayload, AdminUsersFilter } from './model';

export const adminUsersQueryKey = ['admin', 'users'] as const;
export const adminUsersListsQueryKey = (context: AdminUsersContext) =>
  [...adminUsersQueryKey, context, 'list'] as const;
export const adminUserAllowedRolesQueryKey = (networkId: string) =>
  [...adminUsersQueryKey, 'network-roles', networkId] as const;

function useAdminUsersClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createAdminUsersClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useAdminUsersQuery(context: AdminUsersContext, filter: AdminUsersFilter) {
  const client = useAdminUsersClient();
  return useQuery({
    queryKey: [...adminUsersListsQueryKey(context), filter],
    queryFn: () => client.list(context, filter),
    enabled: filter.statuses.length > 0,
  });
}

export function useAdminUserStatusCountsQuery(context: AdminUsersContext) {
  const client = useAdminUsersClient();
  return useQuery({
    queryKey: [...adminUsersListsQueryKey(context), { statusCounts: true }],
    // Main list is membership-scoped; its legacy count endpoint still uses user.network.
    enabled: context === AdminUsersContext.SuperAdmin,
    queryFn: () => client.getStatusCounts(context),
  });
}

export function useAdminUserMutations(context: AdminUsersContext) {
  const client = useAdminUsersClient();
  const queryClient = useQueryClient();
  const invalidateLists = () => {
    void queryClient.invalidateQueries({ queryKey: adminUsersListsQueryKey(context) });
  };
  return {
    create: useMutation({
      mutationFn: ({ payload }: { payload: AdminUserMutationPayload }) =>
        client.create(context, payload),
      onSuccess: invalidateLists,
    }),
    update: useMutation({
      mutationFn: ({ user, payload }: { user: AdminUser; payload: AdminUserMutationPayload }) =>
        client.update(context, user, payload),
      onSuccess: invalidateLists,
    }),
    setEnabled: useMutation({
      mutationFn: ({ username, enabled }: { username: string; enabled: boolean }) =>
        client.setEnabled(context, username, enabled),
      onSuccess: invalidateLists,
    }),
  };
}

export function useAdminUserNetworksQuery(enabled: boolean) {
  const client = useAdminUsersClient();
  return useQuery({
    queryKey: [...adminUsersQueryKey, 'networks'],
    queryFn: client.listNetworks,
    enabled,
  });
}

export function useAdminUserAllowedRolesQuery(networkId: string) {
  const client = useAdminUsersClient();
  return useQuery({
    queryKey: adminUserAllowedRolesQueryKey(networkId),
    queryFn: () => client.listAllowedRoles(networkId),
    enabled: Boolean(networkId),
  });
}

export function useAdminUserAllowedRolesQueries(networkIds: string[]) {
  const client = useAdminUsersClient();
  return useQueries({
    queries: networkIds.map((networkId) => ({
      queryKey: adminUserAllowedRolesQueryKey(networkId),
      queryFn: () => client.listAllowedRoles(networkId),
    })),
  });
}

export function useAdminUserPublisherOptions({
  context,
  networkId,
  enabled,
}: {
  context: AdminUsersContext;
  networkId: string;
  enabled: boolean;
}) {
  const client = useAdminUsersClient();
  const publishers = useQuery({
    queryKey: [...adminUsersQueryKey, 'publishers', context, networkId],
    queryFn: () => client.listPublishers(context, networkId),
    enabled: enabled && Boolean(networkId),
  });
  return {
    options: publishers.data?.content ?? [],
    loading: publishers.isFetching,
    error: publishers.isError,
  };
}
